// Card brand detection utility
export const getCardBrand = (cardNumber) => {
  if (!cardNumber) return 'unknown';

  // Extract the visible digits from masked card numbers
  // Handles formats like: "411111********11", "**** **** **** 1234", "4111 11** **** 1111"
  let visibleDigits = '';

  if (cardNumber.includes('*')) {
    // Extract all non-asterisk digits to get the visible parts
    visibleDigits = cardNumber.replace(/[*\s-]/g, '');

    // If we don't have enough visible digits to determine brand, return unknown
    if (visibleDigits.length < 2) {
      return 'unknown';
    }
  } else {
    // Remove spaces, dashes, and other non-digits for full card numbers
    visibleDigits = cardNumber.replace(/\D/g, '');
  }

  // Visa - 以 4 开头
  if (/^4/.test(visibleDigits)) {
    return 'visa';
  }

  // Mastercard - 51-55 或 2221-2720
  if (/^5[1-5]/.test(visibleDigits)) {
    return 'mastercard';
  }
  if (/^2[2-7]/.test(visibleDigits) && visibleDigits.length >= 4) {
    const prefix = visibleDigits.substring(0, 4);
    if (prefix >= '2221' && prefix <= '2720') {
      return 'mastercard';
    }
  }

  // American Express - 34 或 37
  if (/^3[47]/.test(visibleDigits)) {
    return 'amex';
  }

  // JCB - 3528-3589 (check before other 35xx patterns)
  if (/^35/.test(visibleDigits) && visibleDigits.length >= 4) {
    const prefix = visibleDigits.substring(0, 4);
    if (prefix >= '3528' && prefix <= '3589') {
      return 'jcb';
    }
  }

  // Diners Club - 300-305、36、38-39
  if (/^30[0-5]/.test(visibleDigits)) {
    return 'diners';
  }
  if (/^36/.test(visibleDigits)) {
    return 'diners';
  }
  if (/^3[89]/.test(visibleDigits)) {
    return 'diners';
  }

  // Discover - 6011、644-649、65 或 622126-622925
  if (/^6011/.test(visibleDigits)) {
    return 'discover';
  }
  if (/^64[4-9]/.test(visibleDigits)) {
    return 'discover';
  }
  if (/^65/.test(visibleDigits)) {
    return 'discover';
  }
  if (/^622/.test(visibleDigits) && visibleDigits.length >= 6) {
    const prefix = visibleDigits.substring(0, 6);
    if (prefix >= '622126' && prefix <= '622925') {
      return 'discover';
    }
  }

  // UnionPay - 62 (but not 622126-622925 which is Discover)
  if (/^62/.test(visibleDigits)) {
    // Check if it's not in the Discover range
    if (visibleDigits.length >= 6) {
      const prefix = visibleDigits.substring(0, 6);
      if (prefix >= '622126' && prefix <= '622925') {
        return 'discover'; // This is actually Discover
      }
    }
    return 'unionpay';
  }

  // Unknown - 其他未匹配类型
  return 'unknown';
};

// Get card brand icon path
export const getCardBrandIcon = (brand) => {
  // This function is not used anymore since we import icons directly in components
  const iconMap = {
    visa: 'visa',
    mastercard: 'mastercard',
    amex: 'amex',
    discover: 'discover',
    diners: 'diners',
    jcb: 'jcb',
    unionpay: 'unionpay',
    unknown: 'unknown'
  };

  return iconMap[brand] || 'unknown';
};

// Check if card is expired
export const isCardExpired = (expiryDate) => {
  if (!expiryDate) return false;

  const [month, year] = expiryDate.split('/');
  if (!month || !year) return false;

  const currentDate = new Date();
  const currentYear = currentDate.getFullYear() % 100; // Get last 2 digits
  const currentMonth = currentDate.getMonth() + 1; // 0-based to 1-based

  const cardYear = parseInt(year, 10);
  const cardMonth = parseInt(month, 10);

  if (cardYear < currentYear) return true;
  if (cardYear === currentYear && cardMonth < currentMonth) return true;

  return false;
};

// Format card number for display (add spaces every 4 digits)
export const formatCardNumber = (cardNumber) => {
  if (!cardNumber) return '';

  // Remove all non-digit characters except asterisks
  const cleaned = cardNumber.replace(/[^\d*]/g, '');

  // Add spaces every 4 characters
  return cleaned.replace(/(.{4})/g, '$1 ').trim();
};

// Get the last 4 digits of a card number (visible digits only)
export const getLastFourDigits = (cardNumber) => {
  if (!cardNumber) return '';

  // Extract only digits (no asterisks)
  const digits = cardNumber.replace(/[^\d]/g, '');

  // Return last 4 digits or all digits if less than 4
  return digits.slice(-4);
};

// Mask card number showing only first 6 and last 4 digits
export const maskCardNumber = (cardNumber) => {
  if (!cardNumber) return '';

  const cleaned = cardNumber.replace(/\D/g, '');

  if (cleaned.length <= 10) {
    // For short numbers, show first 4 and last 4
    return cleaned.slice(0, 4) + '****' + cleaned.slice(-4);
  }

  // Standard format: show first 6 and last 4
  const first6 = cleaned.slice(0, 6);
  const last4 = cleaned.slice(-4);
  const middleLength = cleaned.length - 10;
  const asterisks = '*'.repeat(middleLength);

  return first6 + asterisks + last4;
};
